import { ref, unref, computed, MaybeRefOrGetter } from 'vue';

import { useMicrophoneStore } from '@/stores/microphone';
import { useToastStore } from '@/stores/toast.js';
import { Nullable } from '@/types/common';
import { ClippingConfig, MicAnalyzerConfig, NoiseFloorConfig } from '@/types/composables';
import {
  DEFAULT_CLIPPING_CONFIG,
  DEFAULT_NOISE_FLOOR_CONFIG,
  MAX_PITCH,
  MIN_PITCH,
  SAMPLE_RATE,
  THRESHOLD_PITCH,
  INITIAL_HINT_BOX_COOLDOWN,
  MAX_SAMPLE_GAIN_COUNT,
  SILENCE_GAIN_THRESHOLD,
  NOISE_FLOOR_THRESHOLD,
  CLIPPING_THRESHOLD,
} from '@/utils/constants/microphone';

/**
 * @module useMicAnalyzer
 * @description 마이크 입력 신호의 품질을 실시간으로 분석하고 상태를 관리하는 컨포저블입니다.
 * ### 주요 개념
 * - Noise Floor (노이즈 플로어): 신호가 유의미한 음성 레벨 이하로 낮아져 배경 잡음과 구분이 어려운 상태를 감지합니다.
 * - Clipping (클리핑): 입력 신호가 하드웨어/소프트웨어 수용 한계치(1.0)를 초과하여 파형이 왜곡이 되는 현상을 감지합니다.
 * - RMS (Root Mean Square): 파형의 제곱 평균의 제곱근으로, 순간적인 피크가 아닌 전체적인 평균 에너지를 의미합니다.
 * - dB (Decibel): 인간의 가청 특성을 고려하여 RMS 에너지를 로그 스케일로 변환한 단위입니다. (공식: 20 * log10(RMS))
 */
export function useMicAnalyzer(config: MaybeRefOrGetter<Partial<MicAnalyzerConfig>> = {}) {
  const microphoneStore = useMicrophoneStore();
  const toastStore = useToastStore();

  const sampleGains = ref<number[]>([]); // 최근 게인 값들의 평균
  const isNoiseFloor = ref<boolean>(false); // 노이즈 플로어 여부
  const isClipping = ref<boolean>(false); // 클리핑 여부

  const noiseFloorTimestamps = ref<number[]>([]);
  const clippingTimestamps = ref<number[]>([]);

  const clippingTimerId = ref<Nullable<number>>(null);
  const noiseFloorTimerId = ref<Nullable<number>>(null);
  const hintBoxCooldown = ref<number>(INITIAL_HINT_BOX_COOLDOWN);

  const micAnalyzerConfig = computed<MicAnalyzerConfig>(() => {
    const newConfig = unref<Partial<MicAnalyzerConfig>>(config);
    return {
      noiseFloor: {
        ...DEFAULT_NOISE_FLOOR_CONFIG,
        ...newConfig.noiseFloor,
      },
      clipping: {
        ...DEFAULT_CLIPPING_CONFIG,
        ...newConfig.clipping,
      },
    };
  });

  const analyzeMicGain = (data: Float32Array, isUsingHintBox = false) => {
    if (!microphoneStore.isMicOn) {
      resetAnalyzer();
    } else {
      captureGainSample(data);
      analyzeGain(data, isUsingHintBox);
    }
  };

  const resetAnalyzer = () => {
    sampleGains.value = [];
    isNoiseFloor.value = false;
    isClipping.value = false;
    noiseFloorTimestamps.value = [];
    clippingTimestamps.value = [];
    hintBoxCooldown.value = INITIAL_HINT_BOX_COOLDOWN;
  };

  const captureGainSample = (data: Float32Array) => {
    let peak = 0;
    for (let i = 0; i < data.length; i++) {
      const abs = Math.abs(data[i]);
      if (abs > peak) peak = abs;
    }

    sampleGains.value.push(peak);
    if (sampleGains.value.length > MAX_SAMPLE_GAIN_COUNT) {
      sampleGains.value.shift();
    }
  };

  const analyzeGain = (data: Float32Array, isUsingHintBox: boolean) => {
    if (getAverageGain(sampleGains.value) < SILENCE_GAIN_THRESHOLD || !isHumanVoice(data)) return;

    if (isNoiseFloorData(data, micAnalyzerConfig.value.noiseFloor)) {
      handleNoiseFloor(isUsingHintBox);
    } else if (isClippingData(data, micAnalyzerConfig.value.clipping)) {
      handleClipping(isUsingHintBox);
    }
  };

  function getAverageGain(gains: number[]): number {
    return gains.length === 0 ? 0 : gains.reduce((a, b) => a + b, 0) / gains.length;
  }

  /**
   * @desc 자기상관(Autocorrelation)을 이용하여 신호의 주기성을 분석하고, 인간의 평균적인 음성 피치 대역에 해당하는지 판별합니다.
   * 용어 정의:
   * - Signal (신호): 오디오 샘플
   * - Lag (지연): 원본 신호를 몇 샘플만큼 뒤로 밀어낸 시간 간격 (샘플 단위)
   * - Correlation (상관관계): 두 신호가 얼마나 유사한지를 나타내는 수치 (-1 ~ 1)
   * - Autocorrelation(자기상관): 신호를 자기 자신과 비교 (원본 vs lag만큼 밀린 신호)
   * - Pitch (피치): 소리의 높낮이를 나타내는 기본 주파수 (Hz 단위)
   *   - 주기가 짧을수록 = lag가 작을수록 = 높은 pitch
   *   - 주기가 길수록 = lag가 클수록 = 낮은 pitch
   */
  const isHumanVoice = (data: Float32Array): boolean => {
    // 1. 전체 신호 에너지(Zero-lag Autocorrelation) 계산
    const signalEnergy = data.reduce((acc, val) => acc + val ** 2, 0);
    if (signalEnergy === 0) return false; // 무음 신호는 즉시 제외

    // 2. 분석할 피치(Pitch) 범위 설정 (기본값: 80Hz ~ 400Hz)
    // 샘플링 레이트를 기준으로 목소리의 주기가 나타날 수 있는 lag(지연시간) 구간을 계산합니다.
    const minLag = Math.floor(SAMPLE_RATE / MAX_PITCH); // 고음(짧은 주기)에 해당하는 최소 lag(지연)
    const maxLag = Math.floor(SAMPLE_RATE / MIN_PITCH); // 저음(긴 주기)에 해당하는 최대 lag(지연)

    // 3. 해당 lag 구간에서 자기상관값(정규화된)을 계산하여 최대 값을 찾는다.
    let peakCorrelation = 0;
    let peakLag = minLag;

    for (let lag = minLag; lag <= maxLag; lag++) {
      let sum = 0;

      // lag에 대해 자기상관 계산: 신호와 lag만큼 이동한 신호의 내적
      for (let i = 0; i < data.length - lag; i++) {
        sum += data[i] * data[i + lag];
      }

      // 정규화: lag=0일 때의 에너지로 나누어줌
      const normalizedCorrelation = sum / signalEnergy;
      if (normalizedCorrelation > peakCorrelation) {
        peakCorrelation = normalizedCorrelation;
        peakLag = lag;
      }
    }
    // 4. 추정된 기본 주파수(pitch)를 계산 (sampleRate / peakLag)
    const detectedPitch = SAMPLE_RATE / peakLag;
    // 5. 임계치 검사: 자기상관값이 일정 수준(여기서는 0.5 이상)이고,
    // 추정된 pitch가 80Hz~400Hz 범위이면 사람 목소리로 판단합니다.
    return peakCorrelation > THRESHOLD_PITCH && detectedPitch >= MIN_PITCH && detectedPitch <= MAX_PITCH;
  };

  /**
   * @desc 전체적인 음량 에너지를 분석하여 노이즈 플로어(저음량 상태) 여부를 판별합니다.
   * @param {Float32Array} data - 분석할 오디오 시간 도메인 데이터
   * @param options - 분석 설정
   * @param {number} [options.minDecibels] - 데시벨 하향선
   * @param {number} [options.maxDecibels] - 데시벨 상향선
   * @returns {boolean} 노이즈플로어 발생 여부
   */
  const isNoiseFloorData = (data: Float32Array, options: NoiseFloorConfig): boolean => {
    const rms = Math.sqrt(data.reduce((acc, v) => acc + v ** 2, 0) / data.length);
    const dB = 20 * Math.log10(rms);
    return dB >= options.minDecibels && dB <= options.maxDecibels;
  };

  /**
   * @desc 오디오 신호가 한계치를 초과하여 파형이 클리핑되는지 판별합니다.
   * @param {Float32Array} data - 분석할 오디오 시간 도메인 데이터
   * @param {Object} [options] - 분석 설정
   * @param {number} [options.threshold] - 클리핑 판정 임계값 (0.0 ~ 1.0)
   * @param {number} [options.density] - 데이터 분석 밀도 (0.0 ~ 1.0)
   * @returns {boolean} 클리핑 발생 여부
   */
  const isClippingData = (data: Float32Array, options: ClippingConfig): boolean => {
    const stride = Math.max(1, Math.floor(1 / options.density));

    let clippingCount = 0;
    let sampledCount = 0;

    for (let i = 0; i < data.length; i += stride) {
      sampledCount++;
      if (Math.abs(data[i]) > 1.0) {
        clippingCount++;
      }
    }
    return clippingCount / sampledCount > options.threshold;
  };

  const handleNoiseFloor = (isUsingHintBox: boolean) => {
    if (isNoiseFloor.value) return;

    const nowTimestamp = Date.now();
    noiseFloorTimestamps.value.push(nowTimestamp);
    noiseFloorTimestamps.value = noiseFloorTimestamps.value.filter(
      (timestamp) => nowTimestamp - timestamp <= NOISE_FLOOR_THRESHOLD
    );

    if (noiseFloorTimestamps.value.length >= 2) {
      if (isUsingHintBox) {
        toastStore.hintBox('mic', 'too_far_mic');
      }
      isNoiseFloor.value = true;
      noiseFloorTimestamps.value = [];

      setTimeout(() => {
        isNoiseFloor.value = false;
        if (hintBoxCooldown.value === 10000 || hintBoxCooldown.value === 20000) {
          hintBoxCooldown.value += INITIAL_HINT_BOX_COOLDOWN;
        }
      }, hintBoxCooldown.value);
    }
  };

  const handleClipping = (isUsingHintBox: boolean) => {
    if (isClipping.value) return;

    const nowTimestamp = Date.now();
    clippingTimestamps.value.push(nowTimestamp);
    clippingTimestamps.value = clippingTimestamps.value.filter(
      (timestamp) => nowTimestamp - timestamp <= CLIPPING_THRESHOLD
    );

    if (clippingTimestamps.value.length >= 2) {
      if (isUsingHintBox) {
        toastStore.hintBox('mic', 'too_near_mic');
      }
      isClipping.value = true;
      clippingTimestamps.value = [];

      setTimeout(() => {
        isClipping.value = false;
        if (hintBoxCooldown.value === 10000 || hintBoxCooldown.value === 20000) {
          hintBoxCooldown.value += INITIAL_HINT_BOX_COOLDOWN;
        }
      }, hintBoxCooldown.value);
    }
  };

  return {
    isNoiseFloor,
    isClipping,
    analyzeMicGain,
  };
}
